import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import t from "@/lang/lang";
import { ConsumableLinkDTO } from "@/services/backend/qpm/monitors/types/monitor-snapshot";
import { Button } from "@/shared/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { Pencil, Plus, Save, X } from "lucide-react";
import { useMemo, useState } from "react";
import { Input } from "@/shared/components/ui/input";
import type { UpdateConsumableOverrideReq } from "@/services/backend/qpm/monitors/types/monitor-commands";
import { ConsumableTypeUtil } from "@/services/backend/qpm/lib/consumable-type-util";
import { ConsumableUnitTypeUtil } from "@/services/backend/qpm/lib/consumable-unit-util";
import { ConsumableCadence } from "@/services/backend/qpm/types/enums";
import { ConsumableCadenceUtil } from "@/services/backend/qpm/lib/consumable-cadence-util";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";

type ConsumableMaterialTableProps = {
  monitorId?: string;
  consumableList: ConsumableLinkDTO[];
  /** Persist override and link settings for a single consumable row. */
  onSaveOverride?: (req: UpdateConsumableOverrideReq) => Promise<void>;
};

export function ConsumableMaterialTable({
  consumableList,
  monitorId,
  onSaveOverride,
}: ConsumableMaterialTableProps) {
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);

  const byId = useMemo(() => {
    const map = new Map<string, ConsumableLinkDTO>();
    consumableList.forEach((it) => {
      map.set(it.consumable.id, it);
    });
    return map;
  }, [consumableList]);

  const options = useMemo(
    () =>
      // Use the currently linked consumables as a minimal "existing consumables" catalogue.
      // If you later add a master-data endpoint for consumables, wire it in here.
      [...consumableList]
        .map((x) => x.consumable)
        .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "")),
    [consumableList],
  );

  const canEdit = !!monitorId && !!onSaveOverride;

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="">{t("Material")}</TableHead>
          <TableHead>{t("Art")}</TableHead>
          <TableHead>{t("Eigenschaften")}</TableHead>
          <TableHead className="text-end">{t("Benötigte Menge")}</TableHead>
          <TableHead className="text-end">{t("Einheit")}</TableHead>
          <TableHead className="text-end">{t("Kadenz")}</TableHead>
          <TableHead className="text-end">{t("Preis")}</TableHead>
          <TableHead className="text-end">{t("Aktion")}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        <ConsumableListTableBody
          consumableList={consumableList}
          canEdit={canEdit}
          editingKey={editingKey}
          setEditingKey={setEditingKey}
          isAdding={isAdding}
          setIsAdding={setIsAdding}
          options={options}
          byId={byId}
          monitorId={monitorId}
          onSaveOverride={onSaveOverride}
        />
      </TableBody>
    </Table>
  );
}

type ConsumableListTableBodyProps = {
  consumableList: ConsumableLinkDTO[];
  canEdit: boolean;
  editingKey: string | null;
  setEditingKey: (v: string | null) => void;
  isAdding: boolean;
  setIsAdding: (v: boolean) => void;
  options: ConsumableLinkDTO["consumable"][];
  byId: Map<string, ConsumableLinkDTO>;
  monitorId?: string;
  onSaveOverride?: (req: UpdateConsumableOverrideReq) => Promise<void>;
};

function ConsumableListTableBody({
  consumableList,
  canEdit,
  editingKey,
  setEditingKey,
  isAdding,
  setIsAdding,
  options,
  byId,
  monitorId,
  onSaveOverride,
}: ConsumableListTableBodyProps) {
  const rowEditing = editingKey !== null;

  const startAdd = () => {
    if (!canEdit) return;
    setIsAdding(true);
    setEditingKey("__new__");
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setIsAdding(false);
  };

  if ((!consumableList || consumableList.length === 0) && !isAdding) {
    return (
      <>
        <TableRow>
          <TableCell colSpan={8} className="text-center">
            -- {t("Es wurden noch keine Verbrauchsmaterialien zugewiesen")} --
          </TableCell>
        </TableRow>
        {canEdit ? (
          <TableRow>
            <TableCell colSpan={8} className="py-1 text-end">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-fit p-1"
                      onClick={startAdd}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {t("Verbrauchsmaterial hinzufügen")}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </TableCell>
          </TableRow>
        ) : null}
      </>
    );
  }

  return (
    <>
      {consumableList.map((item) => {
        const key = item.consumable.id;
        const isEdit = editingKey === key;
        if (isEdit) {
          return (
            <ConsumableEditRow
              key={key}
              initial={byId.get(key)}
              isNew={false}
              canEdit={canEdit}
              onCancel={cancelEdit}
              onSaved={cancelEdit}
              monitorId={monitorId}
              onSaveOverride={onSaveOverride}
              options={options}
              existingIds={new Set(consumableList.map((x) => x.consumable.id))}
            />
          );
        }

        return (
          <TableRow key={`${item.consumable.id}-${item.linkedAt}`}>
            <TableCell className="py-1">{item.consumable.name}</TableCell>
            <TableCell className="py-1">
              {item.consumable.type
                ? ConsumableTypeUtil.toString(item.consumable.type)
                : "—"}
            </TableCell>
            <TableCell className="py-1">{item.consumable.sku}</TableCell>
            <TableCell className="py-1 text-end">
              {item.quantityPerDevice ?? item.perDevice ?? 0}
            </TableCell>
            <TableCell className="py-1 text-end">
              {item.consumable.unit
                ? ConsumableUnitTypeUtil.toString(item.consumable.unit)
                : "—"}
            </TableCell>
            <TableCell className="py-1 text-end">
              {item.cadence
                ? ConsumableCadenceUtil.toLongString(item.cadence)
                : "—"}
            </TableCell>
            <TableCell className="py-1 text-end">
              {item.overridePrice?.price != null
                ? item.overridePrice.price
                : "—"}
            </TableCell>
            <TableCell className="py-1 text-end">
              {canEdit ? (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-fit p-1"
                        onClick={() => setEditingKey(key)}
                        disabled={rowEditing}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{t("Bearbeiten")}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                "—"
              )}
            </TableCell>
          </TableRow>
        );
      })}

      {isAdding && editingKey === "__new__" ? (
        <ConsumableEditRow
          key="__new__"
          isNew
          canEdit={canEdit}
          onCancel={cancelEdit}
          onSaved={cancelEdit}
          monitorId={monitorId}
          onSaveOverride={onSaveOverride}
          options={options}
          initial={undefined}
          existingIds={new Set(consumableList.map((x) => x.consumable.id))}
        />
      ) : null}

      {canEdit ? (
        <TableRow>
          <TableCell colSpan={8} className="py-1 text-end">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-fit p-1"
                    disabled={rowEditing}
                    onClick={startAdd}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {t("Verbrauchsmaterial hinzufügen")}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </TableCell>
        </TableRow>
      ) : null}
    </>
  );
}

function toNum(v: string): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

type ConsumableEditRowProps = {
  initial: ConsumableLinkDTO | undefined;
  isNew: boolean;
  canEdit: boolean;
  monitorId?: string;
  onSaveOverride?: (req: UpdateConsumableOverrideReq) => Promise<void>;
  onCancel: () => void;
  onSaved: () => void;
  options: ConsumableLinkDTO["consumable"][];
  existingIds: Set<string>;
};

function ConsumableEditRow({
  initial,
  isNew,
  canEdit,
  monitorId,
  onSaveOverride,
  onCancel,
  onSaved,
  options,
  existingIds,
}: ConsumableEditRowProps) {
  const [selectedId, setSelectedId] = useState<string>(
    initial?.consumable.id ?? "",
  );

  const selected = useMemo(() => {
    if (!selectedId) return undefined;
    return options.find((o) => o.id === selectedId);
  }, [options, selectedId]);

  const [quantityPerDevice, setQuantityPerDevice] = useState<string>(
    initial ? String(initial.quantityPerDevice ?? initial.perDevice ?? 0) : "0",
  );
  const [cadence, setCadence] = useState<ConsumableCadence>(
    (initial?.cadence as ConsumableCadence) ??
      ("EVERY_SERVICE" as ConsumableCadence),
  );
  const [price, setPrice] = useState<string>(
    initial?.overridePrice?.price != null
      ? String(initial.overridePrice.price)
      : "",
  );
  const [note, setNote] = useState<string>(initial?.note ?? "");

  const isDuplicate = isNew && !!selectedId && existingIds.has(selectedId);

  const canSave =
    canEdit && !!monitorId && !!onSaveOverride && !!selectedId && !isDuplicate;

  const renderConsumableCell = () => (
    <select
      className="border-input bg-background h-8 w-full rounded-md border px-2 text-sm"
      value={selectedId}
      onChange={(e) => setSelectedId(e.target.value)}
    >
      <option value="">{t("Bitte auswählen")}</option>
      {options.map((o) => (
        <option key={o.id} value={o.id}>
          {o.name} | {ConsumableTypeUtil.toString(o.type)} |{" "}
          {o.basePrice?.price ?? "—"}
        </option>
      ))}
    </select>
  );

  const onSave = async () => {
    if (!canSave || !monitorId || !onSaveOverride) return;

    await onSaveOverride({
      monitorId,
      consumableId: selectedId,
      quantityPerDevice: toNum(quantityPerDevice),
      cadence,
      // legacy
      perDevice: toNum(quantityPerDevice),
      perYear: 0,
      note,
      overrideEffective: price.trim().length > 0,
      overridePrice: price.trim().length > 0 ? toNum(price) : undefined,
    });

    onSaved();
  };

  const onValueChangeCadence = (value: ConsumableCadence) => {
    setCadence(value);
  };

  return (
    <TableRow>
      <TableCell className="py-1">{renderConsumableCell()}</TableCell>
      <TableCell className="py-1">
        {(() => {
          const type = selected?.type ?? initial?.consumable.type;
          return type ? ConsumableTypeUtil.toString(type) : "—";
        })()}
      </TableCell>
      <TableCell className="py-1">
        {selected?.sku ?? initial?.consumable.sku ?? "—"}
      </TableCell>
      <TableCell className="py-1 text-end">
        <Input
          className="h-8 text-right"
          value={quantityPerDevice}
          onChange={(e) => setQuantityPerDevice(e.target.value)}
        />
      </TableCell>
      <TableCell className="py-1 text-end">
        {(() => {
          const unit = selected?.unit ?? initial?.consumable.unit;
          return unit ? ConsumableUnitTypeUtil.toString(unit) : "—";
        })()}
      </TableCell>
      <TableCell className="py-1 text-end">
        <Select value={cadence} onValueChange={onValueChangeCadence}>
          <SelectTrigger className="w-full max-w-48">
            <SelectValue placeholder={t("Kadenz wählen")} />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem value={ConsumableCadence.Never}>
                {ConsumableCadenceUtil.toLongString(ConsumableCadence.Never)}
              </SelectItem>
              <SelectItem value={ConsumableCadence.EveryService}>
                {ConsumableCadenceUtil.toLongString(
                  ConsumableCadence.EveryService,
                )}
              </SelectItem>
              <SelectItem value={ConsumableCadence.Every2ndService}>
                {ConsumableCadenceUtil.toLongString(
                  ConsumableCadence.Every2ndService,
                )}
              </SelectItem>
              <SelectItem value={ConsumableCadence.OncePerYear}>
                {ConsumableCadenceUtil.toLongString(
                  ConsumableCadence.OncePerYear,
                )}
              </SelectItem>
            </SelectGroup>
          </SelectContent>
        </Select>
      </TableCell>
      <TableCell className="py-1 text-end">
        <Input
          className="h-8 text-right"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          placeholder="—"
        />
      </TableCell>
      <TableCell className="py-1">
        <Input
          className="h-8"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={t("Notiz")}
        />
      </TableCell>
      <TableCell className="py-1 text-end">
        <TooltipProvider>
          {/* Cancel */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                className="mr-1 h-fit p-1"
                size="sm"
                variant="outline"
                onClick={onCancel}
              >
                <X className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t("Abbrechen")}</TooltipContent>
          </Tooltip>
          {/* Save */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant="default"
                className="h-fit p-1"
                onClick={onSave}
                disabled={!canSave}
              >
                <Save className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {isDuplicate ? t("Bereits hinzugefügt") : t("Speichern")}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </TableCell>
    </TableRow>
  );
}
